import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/site/Logo";
import { CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/lib/api";

const links = [
  { label: "Leistungen", id: "leistungen" },
  { label: "Rechner", id: "rechner" },
  { label: "Über uns", id: "ueber-uns" },
  { label: "Referenzen", id: "referenzen" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy: markiert den Abschnitt, der gerade im oberen Viewport-Drittel liegt
  useEffect(() => {
    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Mobiles Menü: Escape schliesst, Hintergrund scrollt nicht mit
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || open ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo-link"
          className="flex items-center"
          aria-label="Sonnenwerk – zum Seitenanfang"
        >
          <Logo />
        </a>

        <nav className="hidden md:flex items-center gap-7" aria-label="Hauptnavigation">
          {links.map((l) => {
            const isActive = active === l.id;
            return (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`nav-link-${l.id}`}
                aria-current={isActive ? "true" : undefined}
                className={`text-sm transition-colors relative group ${
                  isActive ? "text-[#1A1D20] font-medium" : "text-[#1A1D20]/70 hover:text-[#1A1D20]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-[#EAB308] transition-all duration-300 ${
                    isActive ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={CONTACT_PHONE_HREF}
            data-testid="nav-phone"
            className="inline-flex items-center gap-2 text-sm text-[#1A1D20]/75 hover:text-[#1A1D20] transition-colors"
          >
            <Phone size={15} className="text-[#EAB308]" aria-hidden="true" />
            {CONTACT_PHONE}
          </a>
          <Button
            asChild
            data-testid="nav-cta-button"
            className="bg-[#1A1D20] text-white hover:bg-[#2A3B32] rounded-full h-10 px-5"
          >
            <a href="#kontakt">Beratung anfordern</a>
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 rounded-md text-[#1A1D20]"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Menü schliessen" : "Menü öffnen"}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div id="mobile-nav" className="md:hidden glass-nav border-t border-black/5">
          <nav className="px-6 py-4 flex flex-col gap-3" aria-label="Mobile Navigation">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`nav-mobile-${l.id}`}
                aria-current={active === l.id ? "true" : undefined}
                className={`py-2 text-sm ${
                  active === l.id ? "text-[#1A1D20] font-medium" : "text-[#1A1D20]/85"
                }`}
                onClick={() => setOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="nav-mobile-phone"
              className="py-2 inline-flex items-center gap-2 text-sm text-[#1A1D20]/85"
            >
              <Phone size={15} className="text-[#EAB308]" aria-hidden="true" />
              {CONTACT_PHONE}
            </a>
            <Button
              asChild
              data-testid="nav-mobile-cta"
              className="bg-[#1A1D20] text-white hover:bg-[#2A3B32] rounded-full h-10"
            >
              <a href="#kontakt" onClick={() => setOpen(false)}>
                Beratung anfordern
              </a>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
